/* eslint-disable */
import React, { useState, useEffect } from "react";
import { Flex, Form, InputNumber } from "antd";
import { useNewTrialState } from "../../../pages/new-trail/hooks/NewTrialStateProvider";
import "./style.less";
import { restrictToTwoDecimals } from "../../../pages/create-protocol/utils";

const StandardBiologicalRate = ({ treatmentIndex, treatmentData }) => {
  const {
    ratesDosage,
    trialInfo,
    setRatesDosage,
    trialProtocolData,
    setIsStandardDataValid,
    setRatesDosageValidation,
  } = useNewTrialState();
  const [biologicalproducts, setBiologicalProducts] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    if (trialProtocolData?.product_details?.biologicals) {
      setBiologicalProducts(trialProtocolData?.product_details?.biologicals);
    }
  }, [trialProtocolData]);

  // --- Biological Plot Validation Function ---
  const isBiologicalPlotValid = React.useCallback(() => {
    if (!trialProtocolData?.protocol_type?.includes("biological")) return true;
    const applications =
      trialProtocolData?.product_details?.biologicals?.applications ?? [];
    const num = applications?.length ? applications?.length : 1;
    const data = ratesDosage?.rates_and_dosages;
    if (!Array.isArray(data)) return false;
    for (const row of data) {
      for (const entry of row) {
        if (
          !entry ||
          !Array.isArray(entry?.bioTreatement) ||
          entry?.bioTreatement.length !== num
        ) {
          return false;
        }
      }
    }
    return true;
  }, [ratesDosage, trialProtocolData]);
  // Initialize form values when biologicalproducts or ratesDosage changes
  useEffect(() => {
    if (biologicalproducts) {
      const standardApplicationSetting = biologicalproducts.treatments.map(
        (product) => ({
          product: product?.products?.[0]?.variety ?? "",
          tank_application_volume: product?.tank_mix_rate?.min ?? 0,
          unit: product?.products?.[0]?.measure_unit ?? "",
        })
      );

      const standard_biological_application_settings =
        Array.isArray(
          trialInfo?.standard_field_properties
            ?.standard_biological_application_settings
        ) &&
        trialInfo.standard_field_properties
          .standard_biological_application_settings.length > 0
          ? trialInfo.standard_field_properties
              .standard_biological_application_settings
          : Array.isArray(
              ratesDosage?.standard_biological_application_settings
            ) && ratesDosage.standard_biological_application_settings.length > 0
          ? ratesDosage.standard_biological_application_settings
          : standardApplicationSetting;

      const standard_tank_mix_rates =
        Array.isArray(
          trialInfo?.standard_field_properties?.standard_tank_mix_rates
        ) &&
        trialInfo.standard_field_properties.standard_tank_mix_rates.length > 0
          ? trialInfo.standard_field_properties.standard_tank_mix_rates
          : Array.isArray(ratesDosage?.standard_tank_mix_rates) &&
            ratesDosage.standard_tank_mix_rates.length > 0
          ? ratesDosage.standard_tank_mix_rates
          : Array.isArray(biologicalproducts?.treatments) &&
            biologicalproducts.treatments.length > 0
          ? biologicalproducts?.treatments.map(
              (treatment) => treatment?.tank_mix_rate?.min ?? 0
            )
          : [0];

      setRatesDosage((prevRatesDosage) => ({
        ...prevRatesDosage,
        standard_biological_application_settings,
        standard_tank_mix_rates,
      }));
    }
  }, [biologicalproducts, trialInfo, setRatesDosage]);

  // Tank volume validation
  useEffect(() => {
    if (!biologicalproducts) return;
    const tankVolume =
      ratesDosage?.standard_biological_application_settings?.[treatmentIndex]
        ?.tank_application_volume;

    const min = treatmentData?.tankMixRate?.min;
    const max = treatmentData?.tankMixRate?.max;
    if (
      tankVolume == null ||
      tankVolume === 0 ||
      tankVolume === "" ||
      (min !== undefined && tankVolume < min) ||
      (max !== undefined && tankVolume > max)
    ) {
      setIsStandardDataValid(false);
    } else {
      setIsStandardDataValid(true);
    }
  }, [
    ratesDosage,
    biologicalproducts,
    setIsStandardDataValid,
    treatmentIndex,
    treatmentData,
  ]);

  // --- Combined Validation ---
  useEffect(() => {
    // You may have your own logic for isSeedPlotValid and isNutriendPlotValid
    const isSeedPlotValid = true;
    const isNutriendPlotValid = true;
    const biologicalValid = isBiologicalPlotValid();
    setRatesDosageValidation(
      isSeedPlotValid && isNutriendPlotValid && biologicalValid
    );
  }, [
    ratesDosage,
    trialProtocolData,
    isBiologicalPlotValid,
    setRatesDosageValidation,
  ]);

  const tankMixRate = treatmentData?.tankMixRate || { min: 0, max: 0 };
  const measureUnit =
    treatmentData?.products?.[0]?.unit ||
    biologicalproducts?.treatments?.[treatmentIndex]?.products?.[0]
      ?.measure_unit;

  const tankApplicationVolume =
    ratesDosage?.standard_tank_mix_rates?.[treatmentIndex] ?? tankMixRate.min;

  useEffect(() => {
    form.setFieldsValue({ tank_application_volume: tankApplicationVolume });
  }, [tankApplicationVolume, form]);

  const handletankRateChange = (tankVolume) => {
    if (tankVolume === null || tankVolume === undefined) return;
    const applicationData = {
      product: treatmentData?.products?.[0]?.name,
      tank_application_volume: tankVolume,
      unit: measureUnit,
    };
    const updatedSettings = Array.isArray(
      ratesDosage?.standard_biological_application_settings
    )
      ? [...ratesDosage.standard_biological_application_settings]
      : [];
    updatedSettings[treatmentIndex] = applicationData;
    setRatesDosage({
      ...ratesDosage,
      standard_biological_application_settings: updatedSettings,
      standard_tank_mix_rates: updatedSettings?.map((setting) =>
        setting?.tank_application_volume != null
          ? setting?.tank_application_volume
          : 0
      ),
    });
  };

  return (
    <div className="standard-seedrate-main biological-rate-main">
      <Flex justify="space-between">
        <div className="productRate-info application-info">
          <Form form={form} layout="vertical" className="tank_form">
            <Form.Item
              name="tank_application_volume"
              label={"Tank Volume"}
              className="input-biologicalrate"
              rules={[
                {
                  validator: (_, value) => {
                    const min = tankMixRate.min;
                    const max = tankMixRate.max;
                    if (value === undefined || value === null || value === "") {
                      return Promise.reject(
                        new Error(`${"Volume cannot be empty"}`)
                      );
                    }
                    if (value < min) {
                      return Promise.reject(
                        new Error(
                          `${"Value out of range. Expected range:"} ${min} - ${max} L/ha`
                        )
                      );
                    }
                    if (value > max) {
                      return Promise.reject(
                        new Error(
                          `${"Value out of range. Expected range:"} ${min} - ${max} L/ha`
                        )
                      );
                    }
                    return Promise.resolve();
                  },
                  validateTrigger: ["onBlur", "onChange"],
                },
              ]}
            >
              <InputNumber
                suffix={"L/ha"}
                precision={2}
                step={0.01}
                onChange={(tankVolume) => {
                  handletankRateChange(tankVolume);
                }}
                onBlur={() => {
                  form.validateFields(["tank_application_volume"]);
                }}
                onKeyDown={(e) => restrictToTwoDecimals(e)}
                className="input-number customized-input-number biological-rate-input"
              />
            </Form.Item>
          </Form>
          <div className="product-info-label">
            {`${tankMixRate.min} L/ha - ${tankMixRate.max} L/ha`}
          </div>
        </div>
      </Flex>
    </div>
  );
};

export default StandardBiologicalRate;
